import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import { ImagePlus } from "lucide-react";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import TextField from "../Form/TextField";
import ColorField from "../Form/ColorField";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startSubmitWhiteLabelBranding } from "../../actions/featureAddons";
import { startUploadFileAws } from "../../services/public";
import { errorCodeToText, hasKnownErrorTranslation } from "../../utils/errors";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  ALERT_ICON_TYPE_ERROR,
  ALERT_ICON_TYPE_SUCCESS,
} from "../../utils/constants";
import {
  BUTTON_CANCEL,
  BUTTON_REMOVE_IMAGE,
  FIELD_REQUIRED,
  LOADING,
  WHITE_LABEL_FORM_BADGE_OPTIONAL,
  WHITE_LABEL_FORM_BADGE_REQUIRED,
  WHITE_LABEL_FORM_COLOR_BUTTONS,
  WHITE_LABEL_FORM_COLOR_PRIMARY,
  WHITE_LABEL_FORM_COLOR_SECONDARY,
  WHITE_LABEL_FORM_DESIRED_DOMAIN,
  WHITE_LABEL_FORM_COLOR_INVALID,
  WHITE_LABEL_FORM_DOMAIN_BLOCKED,
  WHITE_LABEL_FORM_DOMAIN_HELP,
  WHITE_LABEL_FORM_DOMAIN_INVALID,
  WHITE_LABEL_FORM_DOMAIN_PLACEHOLDER,
  WHITE_LABEL_FORM_DROP_HINT,
  WHITE_LABEL_FORM_DROP_TITLE,
  WHITE_LABEL_FORM_FAVICON,
  WHITE_LABEL_FORM_FAVICON_HELP,
  WHITE_LABEL_FORM_INTRO,
  WHITE_LABEL_FORM_INVALID_FILE,
  WHITE_LABEL_FORM_LOGO,
  WHITE_LABEL_FORM_LOGO_LOGIN,
  WHITE_LABEL_FORM_LOGO_LOGIN_HELP,
  WHITE_LABEL_FORM_LOGO_NAVBAR,
  WHITE_LABEL_FORM_LOGO_NAV_HELP,
  WHITE_LABEL_FORM_NAME_BUSINESS,
  WHITE_LABEL_FORM_NAME_INVALID,
  WHITE_LABEL_FORM_NAME_PLACEHOLDER,
  WHITE_LABEL_FORM_NOTES,
  WHITE_LABEL_FORM_NOTES_INVALID,
  WHITE_LABEL_FORM_NOTES_PLACEHOLDER,
  WHITE_LABEL_FORM_SECTION_COLORS,
  WHITE_LABEL_FORM_SECTION_COLORS_HELP,
  WHITE_LABEL_FORM_SECTION_DOMAIN,
  WHITE_LABEL_FORM_SECTION_GENERAL,
  WHITE_LABEL_FORM_SECTION_VISUAL,
  WHITE_LABEL_FORM_SECTION_VISUAL_HELP,
  WHITE_LABEL_FORM_SUBMIT,
  WHITE_LABEL_FORM_SUBMITTED,
  WHITE_LABEL_FORM_TITLE,
} from "../../i18n/keysTranslations";
import {
  EMPTY_WHITE_LABEL_BRANDING_FORM,
  brandingToFormValues,
  validateWhiteLabelBranding,
} from "../../utils/whiteLabelAddon";

const OPTIONAL_RULES = { required: false };
const CARD_SX = {
  width: "100%",
  boxSizing: "border-box",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  borderRadius: 2,
  p: 2,
  backgroundColor: "background.paper",
  cursor: "pointer",
};

const StatusBadge = ({ label, tone = "optional" }) => (
  <Typography
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1,
      px: 1,
      py: 0.6,
      borderRadius: "999px",
      color: tone === "required" ? "primary.main" : "text.secondary",
      backgroundColor:
        tone === "required" ? "rgba(37, 99, 235, 0.12)" : "action.hover",
    }}
  >
    {label}
  </Typography>
);

const SectionHeading = ({ title, help }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
    {help ? (
      <Typography variant="body2" color="text.secondary">
        {help}
      </Typography>
    ) : null}
  </Box>
);

const VALIDATION_MESSAGE_KEY = {
  required: FIELD_REQUIRED,
  blockedDomain: WHITE_LABEL_FORM_DOMAIN_BLOCKED,
  invalidDomain: WHITE_LABEL_FORM_DOMAIN_INVALID,
  invalidColor: WHITE_LABEL_FORM_COLOR_INVALID,
  invalidName: WHITE_LABEL_FORM_NAME_INVALID,
  invalidNotes: WHITE_LABEL_FORM_NOTES_INVALID,
  invalidFile: WHITE_LABEL_FORM_INVALID_FILE,
};

const FIELD_FALLBACK_REASON = {
  nameBusiness: "invalidName",
  logo: "invalidFile",
  logoNavbar: "invalidFile",
  logoLogin: "invalidFile",
  favicon: "invalidFile",
  primaryColor: "invalidColor",
  secondaryColor: "invalidColor",
  primaryButtonColor: "invalidColor",
  desiredDomain: "invalidDomain",
  notes: "invalidNotes",
};

const validationMessage = ({ reason, field, t }) => {
  const key =
    VALIDATION_MESSAGE_KEY[reason] ||
    VALIDATION_MESSAGE_KEY[FIELD_FALLBACK_REASON[field]];
  return key ? t(key) : t(FIELD_REQUIRED);
};

const LogoDropzone = ({
  name,
  title,
  help,
  required = false,
  layout = "compact",
  control,
  errors,
  setValue,
  setError,
  clearErrors,
  agencyID,
  onUploadingChange = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const value = useWatch({ control, name }) || "";
  const fieldError = errors?.[name]?.message || "";

  const uploadFile = async (file) => {
    if (!file || !agencyID) return;
    if (!file.type?.startsWith("image/")) {
      setError(name, { message: t(WHITE_LABEL_FORM_INVALID_FILE) });
      return;
    }
    setIsUploading(true);
    onUploadingChange({ name, isUploading: true });
    const url = await dispatch(
      startUploadFileAws(
        file,
        file.type,
        `white-label/${agencyID}/`,
        true,
        false
      )
    );
    setIsUploading(false);
    onUploadingChange({ name, isUploading: false });
    if (typeof url === "string" && url) {
      setValue(name, url, { shouldDirty: true, shouldValidate: true });
      clearErrors(name);
    }
  };

  const onPickFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    await uploadFile(file);
  };

  const onDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    await uploadFile(event.dataTransfer?.files?.[0]);
  };

  return (
    <Box
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
      onClick={() => {
        if (!isUploading) inputRef.current?.click();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (!isUploading) inputRef.current?.click();
        }
      }}
      sx={{
        ...CARD_SX,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1.5,
        pointerEvents: isUploading ? "none" : "auto",
        borderColor: fieldError
          ? "error.main"
          : isDragging
            ? "text.primary"
            : INPUT_COLOR_BORDER,
        minHeight: layout === "hero" ? 180 : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: layout === "hero" ? "space-between" : "flex-end",
          width: "100%",
          minHeight: 24,
        }}
      >
        {layout === "hero" ? (
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            {title}
          </Typography>
        ) : null}
        <StatusBadge
          label={t(
            required
              ? WHITE_LABEL_FORM_BADGE_REQUIRED
              : WHITE_LABEL_FORM_BADGE_OPTIONAL
          )}
          tone={required ? "required" : "optional"}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          textAlign: "center",
          width: "100%",
          py: layout === "hero" ? 1 : 0.5,
        }}
      >
        {isUploading ? (
          <CircularProgress size={22} />
        ) : value ? (
          <Box
            component="img"
            src={value}
            alt=""
            sx={{
              maxHeight: layout === "hero" ? 72 : 48,
              maxWidth: 160,
              objectFit: "contain",
            }}
          />
        ) : (
          <ImagePlus size={22} strokeWidth={1.75} />
        )}
        {isUploading ? (
          <Typography variant="caption" color="text.secondary">
            {t(LOADING)}
          </Typography>
        ) : null}
        {layout === "hero" && !value && !isUploading ? (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
              {t(WHITE_LABEL_FORM_DROP_TITLE)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(WHITE_LABEL_FORM_DROP_HINT)}
            </Typography>
          </>
        ) : null}
        {layout !== "hero" && !isUploading ? (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
              {title}
            </Typography>
            {help ? (
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
              >
                {help}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {!required && value ? (
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={(event) => {
              event.stopPropagation();
              setValue(name, "", { shouldDirty: true, shouldValidate: true });
              clearErrors(name);
            }}
            sx={{
              border: 0,
              background: "none",
              cursor: "pointer",
              color: "text.secondary",
              p: 0,
            }}
          >
            {t(BUTTON_REMOVE_IMAGE)}
          </Typography>
        ) : null}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        hidden
        onChange={onPickFile}
      />
      {fieldError ? (
        <Typography variant="caption" color="error" sx={{ width: "100%" }}>
          {fieldError}
        </Typography>
      ) : null}
    </Box>
  );
};

const WhiteLabelBrandingModal = ({
  modalOpen = false,
  onCloseModal = () => {},
  branding = null,
  onSubmitted = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const agencyID = useSelectorApp((state) => state.auth.user?.agencyID);
  const brandingRef = useRef(branding);
  brandingRef.current = branding;
  const [uploadingFields, setUploadingFields] = useState({});
  const isUploadingLogo = Object.values(uploadingFields).some(Boolean);
  const onUploadingChange = ({ name, isUploading }) =>
    setUploadingFields((current) => ({ ...current, [name]: isUploading }));
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { ...EMPTY_WHITE_LABEL_BRANDING_FORM },
  });

  useEffect(() => {
    if (!modalOpen) return;
    reset(brandingToFormValues(brandingRef.current));
  }, [modalOpen, reset]);

  const applyBrandingValidationErrors = (fieldErrors) => {
    Object.entries(fieldErrors).forEach(([field, reason]) => {
      setError(field, {
        message: validationMessage({ reason, field, t }),
      });
    });
  };

  const onSubmit = async (values) => {
    clearErrors();
    const parsed = validateWhiteLabelBranding(values);
    if (!parsed.ok) {
      applyBrandingValidationErrors(parsed.fieldErrors || {});
      return;
    }
    const result = await dispatch(
      startSubmitWhiteLabelBranding(parsed.branding)
    );
    if (result?.ok) {
      SimpleAlert({
        title: t(WHITE_LABEL_FORM_SUBMITTED),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      onSubmitted();
      onCloseModal();
      return;
    }
    const field = result?.data?.field;
    if (result?.code === "whiteLabelInvalidBranding" && field) {
      setError(field, {
        message: validationMessage({
          reason: FIELD_FALLBACK_REASON[field],
          field,
          t,
        }),
      });
      return;
    }
    const translated = hasKnownErrorTranslation(result?.code)
      ? errorCodeToText(result.code)
      : "";
    const title = translated || result?.message;
    if (title) {
      SimpleAlert({
        title,
        icon: ALERT_ICON_TYPE_ERROR,
      });
    }
  };

  const logoProps = {
    control,
    errors,
    setValue,
    setError,
    clearErrors,
    agencyID,
    onUploadingChange,
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(WHITE_LABEL_FORM_TITLE)}
      description={t(WHITE_LABEL_FORM_INTRO)}
      variantDescription="body2"
      sxDescription={{ color: "text.secondary", lineHeight: 1.5 }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      enableHistoryManagement={false}
      ariaLabel={t(WHITE_LABEL_FORM_TITLE)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <SectionHeading title={t(WHITE_LABEL_FORM_SECTION_GENERAL)} />
          <TextField
            control={control}
            name="nameBusiness"
            variant="filled"
            label={t(WHITE_LABEL_FORM_NAME_BUSINESS)}
            placeholder={t(WHITE_LABEL_FORM_NAME_PLACEHOLDER)}
            rules={OPTIONAL_RULES}
            errors={errors}
            fullWidth
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <SectionHeading
            title={t(WHITE_LABEL_FORM_SECTION_VISUAL)}
            help={t(WHITE_LABEL_FORM_SECTION_VISUAL_HELP)}
          />
          <LogoDropzone
            {...logoProps}
            name="logo"
            title={t(WHITE_LABEL_FORM_LOGO)}
            required
            layout="hero"
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <LogoDropzone
              {...logoProps}
              name="logoNavbar"
              title={t(WHITE_LABEL_FORM_LOGO_NAVBAR)}
              help={t(WHITE_LABEL_FORM_LOGO_NAV_HELP)}
            />
            <LogoDropzone
              {...logoProps}
              name="logoLogin"
              title={t(WHITE_LABEL_FORM_LOGO_LOGIN)}
              help={t(WHITE_LABEL_FORM_LOGO_LOGIN_HELP)}
            />
          </Box>
          <LogoDropzone
            {...logoProps}
            name="favicon"
            title={t(WHITE_LABEL_FORM_FAVICON)}
            help={t(WHITE_LABEL_FORM_FAVICON_HELP)}
            layout="favicon"
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <SectionHeading
            title={t(WHITE_LABEL_FORM_SECTION_COLORS)}
            help={t(WHITE_LABEL_FORM_SECTION_COLORS_HELP)}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 1.5,
            }}
          >
            <ColorField
              control={control}
              name="primaryColor"
              label={t(WHITE_LABEL_FORM_COLOR_PRIMARY)}
              rules={OPTIONAL_RULES}
              errors={errors}
              fullWidth
            />
            <ColorField
              control={control}
              name="secondaryColor"
              label={t(WHITE_LABEL_FORM_COLOR_SECONDARY)}
              rules={OPTIONAL_RULES}
              errors={errors}
              fullWidth
            />
            <ColorField
              control={control}
              name="primaryButtonColor"
              label={t(WHITE_LABEL_FORM_COLOR_BUTTONS)}
              rules={OPTIONAL_RULES}
              errors={errors}
              fullWidth
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <SectionHeading title={t(WHITE_LABEL_FORM_SECTION_DOMAIN)} />
          <TextField
            control={control}
            name="desiredDomain"
            variant="filled"
            label={t(WHITE_LABEL_FORM_DESIRED_DOMAIN)}
            placeholder={t(WHITE_LABEL_FORM_DOMAIN_PLACEHOLDER)}
            helperText={t(WHITE_LABEL_FORM_DOMAIN_HELP)}
            rules={OPTIONAL_RULES}
            errors={errors}
            fullWidth
          />
          <TextField
            control={control}
            name="notes"
            variant="filled"
            label={t(WHITE_LABEL_FORM_NOTES)}
            placeholder={t(WHITE_LABEL_FORM_NOTES_PLACEHOLDER)}
            rules={OPTIONAL_RULES}
            errors={errors}
            fullWidth
            multiline
            minRows={3}
            showCounterCharacters
            maxCharacters={500}
          />
        </Box>
      </Box>
      <DialogActions sx={{ p: 0, pt: 2 }}>
        <ContainerModalActions
          hasPadding
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_CANCEL,
            disabled: isSubmitting,
          }}
          propsPrimaryButton={{
            showButton: true,
            onClick: handleSubmit(onSubmit),
            text: WHITE_LABEL_FORM_SUBMIT,
            type: "button",
            loading: isSubmitting,
            disabled: isUploadingLogo,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default WhiteLabelBrandingModal;
